import { type ApiClient, listQueryParams } from "./client";
import type {
  ListQuery,
  OfferDriver,
  OfferDriverCatalog,
  OfferIntegrationDetail,
  OfferIntegrationInput,
  OfferIntegrationPatch,
  OfferIntegrationSummary,
  OfferRun,
  OfferRunDetail,
  OfferRunFilters,
  OpportunityOfferRuns,
  Page,
  StoreDetail,
} from "./types";

/** Offer API integrations: domain operations the UI depends on. Implementations: HTTP (below) and mock (src/api/mock). */
export interface OfferApiApi {
  /** The connectors, and whether an organization can have only one integration. */
  drivers(): Promise<OfferDriverCatalog>;
  list(filter?: { organizationId?: string }): Promise<OfferIntegrationSummary[]>;
  get(id: string): Promise<OfferIntegrationDetail>;
  create(input: OfferIntegrationInput): Promise<OfferIntegrationDetail>;
  update(id: string, patch: OfferIntegrationPatch): Promise<OfferIntegrationDetail>;
  /** Turn the integration on for a store (with its id at the provider when needed). */
  linkStore(storeId: string, integrationId: string, externalStoreId: string | null): Promise<StoreDetail>;
  unlinkStore(storeId: string, integrationId: string): Promise<StoreDetail>;
  /** The run log: one run per call, newest first (steps on `run`). */
  runs(integrationId: string, query: ListQuery<OfferRunFilters>): Promise<Page<OfferRun>>;
  run(id: string): Promise<OfferRunDetail>;
  /** One lead's Offer API log (every integration), and whether a request is waiting to run. */
  opportunityRuns(opportunityId: string, query: ListQuery<OfferRunFilters>): Promise<OpportunityOfferRuns>;
  /** Run the Offer API again for a failed run's lead (a new run is queued). */
  retryRun(id: string): Promise<{ valuationId: string | null }>;
}

export function createHttpOfferApiApi(client: ApiClient): OfferApiApi {
  return {
    drivers: async () => {
      const res = await client.call<{ items: OfferDriver[]; onePerOrganization?: boolean }>("listOfferDrivers");
      return { items: res.items, onePerOrganization: res.onePerOrganization ?? false };
    },
    list: async (filter) =>
      (await client.call<{ items: OfferIntegrationSummary[] }>("listOfferIntegrations", { query: { organizationId: filter?.organizationId } })).items,
    get: (id) => client.call("getOfferIntegration", { params: { id } }),
    create: (input) => client.call("createOfferIntegration", { body: input }),
    update: (id, patch) => client.call("updateOfferIntegration", { params: { id }, body: patch }),
    linkStore: (storeId, integrationId, externalStoreId) =>
      client.call("linkStoreOfferIntegration", { params: { id: storeId, integrationId }, body: { externalStoreId } }),
    unlinkStore: (storeId, integrationId) => client.call("unlinkStoreOfferIntegration", { params: { id: storeId, integrationId } }),
    runs: (integrationId, q) => client.call("listOfferRuns", { params: { id: integrationId }, query: listQueryParams(q) }),
    opportunityRuns: (opportunityId, q) => client.call("listOpportunityOfferRuns", { params: { id: opportunityId }, query: listQueryParams(q) }),
    run: (id) => client.call("getOfferRun", { params: { id } }),
    retryRun: (id) => client.call("retryOfferRun", { params: { id } }),
  };
}
